import { Dialogs, TokenReplacement } from 'dspx-core';

export class APPLICATION {
  static readonly title = 'Survey Portal';
  static readonly logo = '/assets/images/sgv-logo.png';
};

export class APP_DATA_KEYS {
  static readonly maxGridRowCount = 'maxGridRowCount';
  static readonly previousModel = 'previousModel';
};

export const OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS: TokenReplacement[] = [
  { old: 'tabs', new: 'card-set-tabs-flat' },
  { old: 'page', new: 'card-set-page' },
  { old: 'page-repeating', new: 'card-set-page-repeating' },
  { old: 'page-info', new: 'card-set-page-info' },
  { old: 'page-list', new: 'card-set-page-list' },
  { old: 'page-overview', new: 'card-set-page-overview' },
  { old: 'section', new: 'card-section-overview' },
  { old: 'section-repeating', new: 'card-section-repeating' },
  { old: 'section-overview', new: 'card-section-overview' }
];

export class FORM_ACTIONS {
  static readonly referenceDataListForm = {
    new: 'new',
    view: 'view',
    edit: 'edit',
    custom: 'custom',
  } as const;
};

export class FORM_CUSTOM_ACTIONS {
  static readonly referenceDataListForm = {
    bulkUpload: 'bulkUpload',
  } as const;
};

export class POP_UP_CODES {
  static readonly common = {
    clientError: Dialogs.CLIENT_ERROR,
    serverError: Dialogs.SERVER_ERROR,
    validationError: Dialogs.VALIDATION,
    saveSuccessful: 'saveSuccessful',
    confirmDocumentDeletion: 'confirmDocumentDeletion'
  } as const;

  static readonly formResponse = {
    saveSuccessful: 'formResponseSaveSuccessful',
    submissionSuccessful: 'formResponseSubmissionSuccessful',
    promptForReferenceCode: 'promptForReferenceCode',
    addRadionuclideSourceNotification: 'addRadionuclideSourceNotification',
    addPermittedRadionuclideForTransferManagementLicence: 'addPermittedRadionuclideForTransferManagementLicence'
  } as const;
};

export class POP_UP_TITLES {
  static readonly formResponse = {
    saveSuccessful: 'Form Saved',
    submissionSuccessful: 'Application received',
    promptForReferenceCode: 'Do you want to submit new form or open saved form?',
    addRadionuclideSourceNotification: 'Add radionuclide',
    addPermittedRadionuclideForTransferManagementLicence: 'Add permitted radionuclide'
  } as const;
};

export class POP_UP_TEXTS {
  static readonly common = {
    confirmDocumentDeletion: '<p>Confirm the deletion of this document?</p>'
  } as const;
  static readonly formResponse = {
    saveSuccessful: `Your reference code is {{referenceCode}}. This saved version will remain available until: {{expiryDateTime}}.
                  If it is not resaved or submitted by this date and time it will be permanently deleted. <br /><br /> Please provide an 
                  email address to receive a link for your saved draft form. You can continue working on the form by accessing the link. `,
    submissionSuccessful: `Thank you. Your submission has been received. <br /><br /> Your reference code is {{referenceCode}}. Please quote your 
                  reference code when enquiring about your submission.`,
    emailPrompt: {
      requiredValidationMessage: 'Your email address is required.',
      emailPatternValidationMessage: 'Enter a valid email address.'
    },
    promptForReferenceCode: { 
      description: `Enter the reference code and click on 'Open Saved form' to continue or create a new one using 'Start new form' button.`,
      requiredValidationMessage: 'Reference code is required to open saved form',
      okButtonText: 'Open saved form',
      cancelButtonText: 'Start new form',
      referenceCodeQutstionLabel: 'Reference Code'
    },
    addDataToDataGrid: {
      okButtonText: 'Add',
      cancelButtonText: 'Cancel'
    }
  } as const;
};

export class DATA_GRID_ADD_NEW_POP_UPS {
  static readonly functionName = {
    addRadionuclideSourceNotification: 'addRadionuclideSourceNotification',
    addPermittedRadionuclideForTransferManagementLicence: 'addPermittedRadionuclideForTransferManagementLicence'
  } as const;
  static readonly popupCodeMapping = {
    [DATA_GRID_ADD_NEW_POP_UPS.functionName.addRadionuclideSourceNotification]: POP_UP_CODES.formResponse.addRadionuclideSourceNotification,
    [DATA_GRID_ADD_NEW_POP_UPS.functionName.addPermittedRadionuclideForTransferManagementLicence]: POP_UP_CODES.formResponse.addPermittedRadionuclideForTransferManagementLicence
  }
};

export class SERVER_ERROR_POP_UP_CODES {
  static readonly saveFormResponse = [];
  static readonly saveReferenceData = [
    'duplicateCategoryCode',
    'duplicateCategoryValue',
    'duplicateCode',
    'duplicateValue',
    'duplicateDisplayOrder',
    'deletedValues',
  ];
};

export class NAV_TAB_NUMBERS {
  static readonly dashboardCommon = {
    grid: 1,
  } as const;

  static readonly referenceDataForm = {
    refDataCategory: 2,
    refDataValue: 3,
  } as const;

  static readonly maintenanceRequestForm = {
    reportMaintenanceIssue: 1,
  } as const;
};

export class PAGE_NUMBERS {
  static readonly dashboardCommon = {
    grid: 1,
  } as const;

  static readonly referenceDataForm = {
    refDataCategory: 1,
    refDataValue: 1,
  } as const;

  static readonly maintenanceRequestForm = {
    maintenanceIssue: 1,
    contactDetails: 2,
  } as const;
};

export class SECTION_CODES {
  static readonly referenceDataForm = {
    viewRefDataCategory: 'viewRefDataCategory',
    refDataCategory: 'refDataCategory',
    refDataValue: 'refDataValue',
  } as const;
};

export class QUESTION_CODES {
  static readonly referenceDataListForm = {
    referenceDataPlaceholder: 'referenceDataPlaceholder',
  } as const;

  static readonly referenceDataForm = {
    referenceCategoryCode: 'categoryCode',
    referenceCategoryText: 'categoryText',
    referenceCategoryIsActive: 'categoryStatus',
  } as const;
};

export class SAVE_RESULT_STATUS {
  static readonly success = 'Success';
};

export class UIDateTimeFormat {
  static readonly date = 'dd/MM/yyyy';
  static readonly dateTime = 'dd/MM/yyyy HH:mm';
  static readonly locale = 'en-AU'
}

export class FIELD_VALIDATION_PATTERNS {
  static readonly email = '^[\\w-.]+@([\\w-]+\\.)+[\\w-]{2,4}$';
};

export const APPLICATION_IDS = {
  s04ApplicationForChangeLicenceOrPermit: '67247443-dec3-4140-19de-08dd602166e5',
  s08ApplicationForALicenceOrPermit: '5cf44bcd-b4f2-4161-a273-06f5b32643b9',
  s48Schedule4ApplicationForRenewal: '20ea5422-0091-496c-80bc-7a3d85bd8b55'
};

export const SCHEDULE_SECTION_TYPE = {
  add: 'add',
  remove: 'remove',
  holding: 'holding'
};

export const FORM_04_PAGE_MAPPINGS = {
  basicInfo: 'page1',
  healthServiceUses: 'page2',
  substanceChanges: 'page3',
  uses: 'page4',
  nameOrOwnership: 'page5',
  Address: 'page6',
  onlyRemoveResponsiblePerson: 'page7',
  responsiblePerson: 'page8',
  existingSubstances: 'page9',
  manufacturing: 'page10',
  healthPractitionerActivities: 'page11',
  authorisedSupply: 'page12',
  storageAndAccess: 'page13',
  transport: 'page14',
  recordsAndAccountability: 'page15',
  destruction: 'page16',
  goodWholesalingPractice: 'page17',
  submit: 'page18',
};

export const FORM_08_PAGE_MAPPINGS = {
  basicInformation: 'page1',
  healthServiceUses: 'page2',
  substances: 'page3',
  useInResearchIndustry: 'page4',
  applicantDetails: 'page5',
  additionalSites: 'page6',
  responsibleperson: 'page7',
  manufacturing: 'page8',
  healthpractitioneractivities: 'page9',
  authorisedsupply: 'page10',
  storageandaccess: 'page11',
  transport: 'page12',
  recordsandaccountability: 'page13',
  destruction: 'page14',
  goodWholesalingPractice: 'page15',
  submission: 'page16',
  formSummary: 'page17',
};

